import { useState } from 'react';

/** His colour table, with Identity and Energetic pulled apart so adjacent lines stay legible. */
export const DIMENSION_COLOR = {
  identity_purpose: '#e0b34a',
  somatic_awareness: '#2dd4bf',
  psychological_regulation: '#818cf8',
  energetic_coherence: '#f97316',
  multidimensional_consciousness: '#a78bfa',
  relational_integration: '#fb7185',
};

const W = 100;

const scale = (axis, points, height) => {
  const step = axis.length > 1 ? W / (axis.length - 1) : W;
  const index = {};
  axis.forEach((a, i) => { index[a.location_id] = i; });
  return points
    .filter((p) => index[p.location_id] !== undefined)
    .map((p) => ({
      ...p,
      axisIndex: index[p.location_id],
      x: index[p.location_id] * step,
      y: height - (p.percentage / 100) * height,
    }));
};

/**
 * One dimension's journey line. Gaps where it wasn't measured are drawn as faint dotted
 * segments so the participant can see the dimension was skipped, not that it dropped.
 */
export const JourneyLine = ({ dimension, axis, height = 44, showBands = false, bands = [],
  onSelect }) => {
  const colour = DIMENSION_COLOR[dimension.dimension] || '#94a3b8';
  const pts = scale(axis, dimension.points || [], height);

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height }}
      data-testid={`journey-line-${dimension.dimension}`}
    >
      {showBands && bands.map((band) => {
        const top = height - (band.max / 100) * height;
        const bottom = height - (band.min / 100) * height;
        return (
          <rect
            key={band.stage}
            x="0" y={top} width={W} height={Math.max(bottom - top, 0.5)}
            fill="#ffffff"
            opacity={0.03 + band.stage * 0.028}
          />
        );
      })}

      {pts.map((point, i) => {
        if (i === 0) return null;
        const prev = pts[i - 1];
        const gapped = point.axisIndex - prev.axisIndex > 1;
        return (
          <line
            key={point.location_id}
            x1={prev.x} y1={prev.y} x2={point.x} y2={point.y}
            stroke={colour}
            strokeWidth={gapped ? 1 : 1.8}
            strokeOpacity={gapped ? 0.3 : 1}
            strokeDasharray={gapped ? '3 3' : undefined}
            vectorEffect="non-scaling-stroke"
          />
        );
      })}

      {pts.map((point, i) => (
        <circle
          key={point.location_id}
          cx={point.x} cy={point.y}
          r={i === pts.length - 1 ? 3 : 2}
          fill={colour}
          className={onSelect ? 'cursor-pointer' : undefined}
          onClick={onSelect ? () => onSelect(point) : undefined}
          data-testid={`journey-point-${dimension.dimension}-${point.location_id}`}
        >
          <title>{`${point.short_label}: ${point.percentage}% — ${point.stage_name}`}</title>
        </circle>
      ))}
    </svg>
  );
};

/** The cluster. `compact` is the snapshot version — no stage bands, no axis labels. */
export const JourneyLines = ({ dimensions = [], axis = [], bands = [], compact = false }) => {
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-3" data-testid={compact ? 'journey-cluster-compact' : 'journey-cluster'}>
      {dimensions.map((d) => {
        const colour = DIMENSION_COLOR[d.dimension];
        const open = selected && selected.dimension === d.dimension;
        return (
          <div
            key={d.dimension}
            className="rounded-xl border border-white/10 bg-[#0a0d1c]/70 px-4 py-3 backdrop-blur-sm"
            data-testid={`journey-dimension-${d.dimension}`}
          >
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colour }} />
                <span className="text-sm text-white/90 truncate">{d.dimension_name}</span>
              </div>
              <div className="text-right shrink-0">
                {d.percentage === null ? (
                  <span className="text-xs text-white/40">Not measured yet</span>
                ) : (
                  <>
                    <span className="text-sm font-semibold text-white">{d.percentage}%</span>
                    <span className="text-xs ml-2" style={{ color: colour }}>{d.stage_name}</span>
                  </>
                )}
              </div>
            </div>

            {d.points?.length > 0 && (
              <JourneyLine
                dimension={d}
                axis={axis}
                bands={bands}
                height={compact ? 30 : 52}
                showBands={!compact}
                onSelect={(point) => setSelected(
                  open && selected.location_id === point.location_id
                    ? null : { ...point, dimension: d.dimension })}
              />
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              {d.carried_forward && d.last_measured_short_label && (
                <span className="text-xs text-white/40" data-testid={`journey-carried-${d.dimension}`}>
                  Last measured: {d.last_measured_short_label}
                </span>
              )}
              {open && (
                <span className="text-xs text-white/70" data-testid="journey-point-detail">
                  {selected.short_label}: {selected.percentage}% — {selected.stage_name}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {!compact && axis.length > 0 && (
        <div className="flex justify-between px-4 pt-1 text-[10px] text-white/30" data-testid="journey-axis">
          {axis.map((a) => (
            <span key={a.location_id} className={a.taken ? 'text-white/60' : undefined}>
              {a.short_label === 'Baseline' ? 'Base' : a.short_label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default JourneyLines;
